import {
  Flex,
  Text,
  keyframes,
  useDisclosure,
  HStack,
} from '@chakra-ui/react';
import { shallow } from 'zustand/shallow';
import { useUIStore } from '#store';
import { getCancellationView } from '../../utils/cancellationView.js';
import ConfidencePanel from './ConfidencePanel.jsx';

const slideUp = keyframes`
  20%, 100% {
    transform: translate3d(0,-115%,-10px);
  }
`;

/**
 * NewsBar - banner above the TrackWidget that surfaces cancellation evidence
 * and opens the ConfidencePanel modal with the full analytics breakdown.
 *
 * Visibility:
 *   - Shows when we have at least one cancellation event for the outing
 *     (via getCancellationView - analytics block preferred, legacy summary
 *     as fallback).
 *   - Otherwise hides entirely. We deliberately don't advertise "now tracking"
 *     for a registered-but-empty outing - the bar only appears once we have
 *     something concrete to show.
 */
export default function NewsBar() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { news } = useUIStore(
    (state) => ({
      news: state.news,
    }),
    shallow
  );

  const view = getCancellationView(news);
  const hasCancellations = !!view;

  if (!hasCancellations && !news?.show) {
    return null;
  }

  const displayText = hasCancellations
    ? view.isLast30Days
      ? `${view.totalSpots.toLocaleString('en-US')} cancellations in the last 30 days`
      : `${view.totalSpots.toLocaleString('en-US')} cancellations detected`
    : news.text;

  if (!displayText) {
    return null;
  }

  return (
    <>
      <Flex
        position="absolute"
        w="100%"
        transform="translateZ(-10px)"
        animation={`${slideUp} 2s linear 0s 1 forwards`}
        left={0}
      >
        <HStack
          w="100%"
          py={[1, 1.5]}
          px={3}
          bg="orange.600"
          borderTopRadius="lg"
          color="white"
          fontSize={['sm', 'md']}
          fontWeight={500}
          justify="center"
          zIndex={2}
          spacing={2}
          cursor={hasCancellations ? 'pointer' : 'default'}
          onClick={hasCancellations ? onOpen : undefined}
          _hover={hasCancellations ? { bg: 'orange.700' } : undefined}
          transition="background 0.15s"
        >
          <Text>{displayText}</Text>
          {hasCancellations && (
            <Text
              as="span"
              textDecoration="underline"
              fontWeight={600}
            >
              See stats →
            </Text>
          )}
        </HStack>
      </Flex>

      {hasCancellations && (
        <ConfidencePanel isOpen={isOpen} onClose={onClose} />
      )}
    </>
  );
}
